import { Shield } from 'lucide-react';

/**
 * Comprehensive data for all ISO/IATF Management System Standards
 * This is the single source of truth for all standard-related content
 */
export const standards = {
  'iso-9001': {
    slug: 'iso-9001',
    code: 'ISO 9001:2015',
    name: 'Quality Management System',
    shortDescription: 'Demonstrate consistent quality and customer satisfaction',
    description: 'ISO 9001:2015 specifies requirements for a quality management system when an organization needs to demonstrate its ability to consistently provide products and services that meet customer and applicable statutory and regulatory requirements.',
    detailedDescription: 'ISO 9001:2015 is the internationally recognized standard for Quality Management Systems (QMS). It provides a framework for organizations to ensure they meet customer and stakeholder needs within statutory and regulatory requirements related to a product or service. The standard is based on a number of quality management principles including a strong customer focus, the involvement of top management, a process approach and continual improvement.',

    color: {
      text: 'text-blue-600',
      bg: 'bg-blue-50',
      border: 'border-blue-200',
      gradient: 'from-blue-600 via-blue-700 to-indigo-800',
      hover: 'hover:bg-blue-100'
    },

    icon: Shield,

    trainingDuration: {
      awareness: '1 day',
      internalAuditor: '1 day',
      leadAuditor: '5 days'
    },

    benefits: [
      'Improved customer satisfaction through consistent quality',
      'Enhanced organizational efficiency and productivity',
      'Better decision making based on evidence',
      'Increased opportunities for business growth',
      'Systematic approach to risk management',
      'Enhanced credibility and competitive advantage',
      'Improved internal communication and morale',
      'Compliance with regulatory requirements'
    ],

    whoShouldAttend: [
      {
        title: 'Quality Professionals',
        description: 'Quality managers, coordinators, and assurance personnel',
        icon: 'Users'
      },
      {
        title: 'Senior Management',
        description: 'CEOs, directors, and top-level executives',
        icon: 'Briefcase'
      },
      {
        title: 'Process Owners',
        description: 'Department heads and process managers',
        icon: 'Target'
      },
      {
        title: 'Auditors & Consultants',
        description: 'Internal auditors and QMS consultants',
        icon: 'Search'
      }
    ],

    keyTopics: {
      awareness: [
        'Introduction to ISO 9001:2015 and its structure',
        'Understanding the Quality Management Principles',
        'Context of the organization and interested parties',
        'Leadership and commitment requirements',
        'Risk-based thinking approach',
        'Process approach and PDCA cycle',
        'Documentation requirements overview',
        'Benefits of implementing ISO 9001'
      ],
      internalAuditor: [
        'Detailed review of ISO 9001:2015 requirements',
        'Planning and preparing for internal audits',
        'Conducting effective audit interviews',
        'Audit evidence collection and sampling',
        'Writing non-conformities and observations',
        'Audit reporting and follow-up procedures',
        'Practical audit exercises and case studies',
        'Audit team roles and responsibilities'
      ],
      leadAuditor: [
        'Advanced audit techniques and methodologies',
        'Leading and managing audit teams',
        'Certification audit process and requirements',
        'Audit program management',
        'Complex non-conformity assessment',
        'Stakeholder management during audits',
        'Professional ethics and auditor conduct',
        'Certification assessment and examination'
      ]
    },

    seo: {
      metaTitle: 'ISO 9001:2015 Training - Quality Management System Certification | ISDE Academy',
      metaDescription: 'Professional ISO 9001:2015 Quality Management System training. Awareness, Internal Auditor, and Lead Auditor certification courses. Expert-led programs for quality excellence.',
      keywords: 'ISO 9001 training, quality management system, ISO 9001 certification, internal auditor course, lead auditor training, QMS training India, quality management training',
      ogImage: '/images/standards/iso-9001-og.jpg'
    }
  },

  'iso-14001': {
    slug: 'iso-14001',
    code: 'ISO 14001:2015',
    name: 'Environmental Management System',
    shortDescription: 'Reduce environmental impact and demonstrate compliance',
    description: 'ISO 14001:2015 specifies requirements for an environmental management system that an organization can use to enhance its environmental performance and meet compliance obligations.',
    detailedDescription: 'ISO 14001:2015 provides a framework for organizations to protect the environment and respond to changing environmental conditions in balance with socio-economic needs. It specifies requirements that enable an organization to achieve the intended outcomes it sets for its environmental management system.',

    color: {
      text: 'text-green-600',
      bg: 'bg-green-50',
      border: 'border-green-200',
      gradient: 'from-green-600 via-green-700 to-emerald-800',
      hover: 'hover:bg-green-100'
    },

    icon: Shield,

    trainingDuration: {
      awareness: '1 day',
      internalAuditor: '1 day',
      leadAuditor: '5 days'
    },

    benefits: [
      'Reduced environmental impact and waste',
      'Improved resource efficiency and cost savings',
      'Enhanced corporate image and stakeholder confidence',
      'Compliance with environmental regulations',
      'Competitive advantage in tender processes',
      'Improved environmental performance',
      'Better environmental risk management',
      'Sustainable business practices'
    ],

    whoShouldAttend: [
      {
        title: 'Environmental Managers',
        description: 'EHS managers and environmental coordinators',
        icon: 'Leaf'
      },
      {
        title: 'Sustainability Officers',
        description: 'CSR and sustainability professionals',
        icon: 'Globe'
      },
      {
        title: 'Operations Managers',
        description: 'Plant and operations management staff',
        icon: 'Factory'
      },
      {
        title: 'Compliance Personnel',
        description: 'Environmental compliance and regulatory staff',
        icon: 'FileCheck'
      }
    ],

    keyTopics: {
      awareness: [
        'Introduction to ISO 14001:2015 standard',
        'Environmental aspects and impacts',
        'Understanding environmental compliance obligations',
        'Environmental policy and objectives',
        'Life cycle thinking and environmental performance',
        'Emergency preparedness and response',
        'Environmental management principles',
        'Benefits of EMS implementation'
      ],
      internalAuditor: [
        'ISO 14001:2015 requirements in detail',
        'Environmental legislation and compliance',
        'Auditing environmental aspects and impacts',
        'Operational controls assessment',
        'Emergency preparedness verification',
        'Environmental performance evaluation',
        'Practical environmental audit exercises',
        'Environmental audit reporting'
      ],
      leadAuditor: [
        'Advanced environmental auditing techniques',
        'Leading environmental audit teams',
        'Complex environmental compliance assessment',
        'Stakeholder engagement in environmental audits',
        'Environmental management system integration',
        'Advanced risk assessment methods',
        'Professional certification requirements',
        'Certification examination'
      ]
    },

    seo: {
      metaTitle: 'ISO 14001:2015 Training - Environmental Management System Certification | ISDE Academy',
      metaDescription: 'Professional ISO 14001:2015 Environmental Management System training. Awareness, Internal Auditor, and Lead Auditor courses for environmental excellence and compliance.',
      keywords: 'ISO 14001 training, environmental management system, EMS certification, environmental auditor training, ISO 14001 certification India, environmental compliance training',
      ogImage: '/images/standards/iso-14001-og.jpg'
    }
  },

  'iso-45001': {
    slug: 'iso-45001',
    code: 'ISO 45001:2018',
    name: 'Occupational Health & Safety Management',
    shortDescription: 'Provide safe and healthy workplaces',
    description: 'ISO 45001:2018 specifies requirements for an occupational health and safety management system to enable organizations to provide safe and healthy workplaces by preventing work-related injury and ill health.',
    detailedDescription: 'ISO 45001:2018 enables organizations of all sizes and industries to manage their OH&S risks and improve their OH&S performance. It provides a framework to increase safety, reduce workplace risks, and enhance health and well-being at work, enabling an organization to proactively improve its OH&S performance.',

    color: {
      text: 'text-red-600',
      bg: 'bg-red-50',
      border: 'border-red-200',
      gradient: 'from-red-600 via-red-700 to-rose-800',
      hover: 'hover:bg-red-100'
    },

    icon: Shield,

    trainingDuration: {
      awareness: '1 day',
      internalAuditor: '1 day',
      leadAuditor: '5 days'
    },

    benefits: [
      'Reduced workplace accidents and incidents',
      'Lower insurance and compensation costs',
      'Improved employee morale and productivity',
      'Compliance with OH&S legal requirements',
      'Enhanced organizational reputation',
      'Better hazard identification and risk control',
      'Improved safety culture',
      'Reduced absenteeism and staff turnover'
    ],

    whoShouldAttend: [
      {
        title: 'Safety Managers',
        description: 'Health and safety managers and coordinators',
        icon: 'Shield'
      },
      {
        title: 'HR Professionals',
        description: 'Human resources and employee welfare staff',
        icon: 'Users'
      },
      {
        title: 'Operations Managers',
        description: 'Production and operations management',
        icon: 'Cog'
      },
      {
        title: 'Safety Officers',
        description: 'Safety officers and supervisors',
        icon: 'AlertTriangle'
      }
    ],

    keyTopics: {
      awareness: [
        'Introduction to ISO 45001:2018',
        'OH&S hazards and risks identification',
        'Worker participation and consultation',
        'OH&S policy and objectives',
        'Legal and other requirements',
        'Emergency preparedness for OH&S',
        'Incident investigation fundamentals',
        'Creating a safety culture'
      ],
      internalAuditor: [
        'ISO 45001:2018 detailed requirements',
        'OH&S hazard identification and risk assessment',
        'Operational controls for OH&S',
        'Incident and non-conformity investigation',
        'Worker consultation and participation verification',
        'OH&S performance monitoring',
        'Practical OH&S audit scenarios',
        'OH&S audit documentation'
      ],
      leadAuditor: [
        'Advanced OH&S auditing methodology',
        'Leading OH&S audit teams',
        'Complex risk assessment evaluation',
        'Legal compliance verification',
        'Worker consultation assessment',
        'OH&S management system integration',
        'Professional auditor competencies',
        'Certification examination process'
      ]
    },

    seo: {
      metaTitle: 'ISO 45001:2018 Training - Occupational Health & Safety Certification | ISDE Academy',
      metaDescription: 'Professional ISO 45001:2018 Occupational Health & Safety Management System training. Complete OH&S certification programs for workplace safety excellence.',
      keywords: 'ISO 45001 training, occupational health safety, OH&S certification, safety management system, ISO 45001 auditor training, workplace safety India, health safety training',
      ogImage: '/images/standards/iso-45001-og.jpg'
    }
  },

  'iso-27001': {
    slug: 'iso-27001',
    code: 'ISO 27001:2022',
    name: 'Information Security Management',
    shortDescription: 'Protect information and manage cyber security risks',
    description: 'ISO 27001:2022 provides requirements for establishing, implementing, maintaining and continually improving an information security management system to protect the confidentiality, integrity and availability of information.',
    detailedDescription: 'ISO 27001:2022 is the leading international standard focused on information security. It provides a systematic approach to managing sensitive company information, ensuring it remains secure through people, processes, and IT systems. The standard helps organizations protect their information assets from threats such as cyber attacks, data breaches, and information theft.',

    color: {
      text: 'text-purple-600',
      bg: 'bg-purple-50',
      border: 'border-purple-200',
      gradient: 'from-purple-600 via-purple-700 to-indigo-800',
      hover: 'hover:bg-purple-100'
    },

    icon: Shield,

    trainingDuration: {
      awareness: '1 day',
      internalAuditor: '2 days',
      leadAuditor: '5 days'
    },

    benefits: [
      'Protection against cyber threats and data breaches',
      'Enhanced customer trust and confidence',
      'Compliance with data protection regulations',
      'Competitive advantage in IT services',
      'Systematic approach to managing sensitive information',
      'Reduced risk of security incidents',
      'Improved business resilience',
      'Better incident response capabilities'
    ],

    whoShouldAttend: [
      {
        title: 'IT Security Managers',
        description: 'Information security and IT managers',
        icon: 'Lock'
      },
      {
        title: 'Data Protection Officers',
        description: 'DPOs and privacy professionals',
        icon: 'Database'
      },
      {
        title: 'IT Professionals',
        description: 'System administrators and IT staff',
        icon: 'Server'
      },
      {
        title: 'Risk Managers',
        description: 'Risk and compliance officers',
        icon: 'AlertCircle'
      }
    ],

    keyTopics: {
      awareness: [
        'Introduction to ISO 27001:2022',
        'Information security fundamentals',
        'Understanding the CIA triad',
        'Information security policy',
        'Risk assessment basics',
        'Annex A controls overview',
        'Incident management introduction',
        'Building security awareness culture'
      ],
      internalAuditor: [
        'ISO 27001:2022 requirements breakdown',
        'Information security risk assessment',
        'Auditing Annex A controls',
        'Access control verification',
        'Incident management review',
        'Business continuity planning audit',
        'Information security audit techniques',
        'Security audit reporting'
      ],
      leadAuditor: [
        'Advanced information security auditing',
        'Leading information security audits',
        'Complex risk assessment evaluation',
        'Technical security controls assessment',
        'Threat and vulnerability analysis',
        'Security governance audit',
        'Professional security auditor certification',
        'Certification examination'
      ]
    },

    seo: {
      metaTitle: 'ISO 27001:2022 Training - Information Security Management Certification | ISDE Academy',
      metaDescription: 'Professional ISO 27001:2022 Information Security Management System training. Cyber security certification programs for data protection and security excellence.',
      keywords: 'ISO 27001 training, information security management, ISMS certification, cyber security training, ISO 27001 auditor, data protection training India, information security certification',
      ogImage: '/images/standards/iso-27001-og.jpg'
    }
  },

  'iso-13485': {
    slug: 'iso-13485',
    code: 'ISO 13485:2016',
    name: 'Medical Devices Quality Management',
    shortDescription: 'Ensure safety and quality of medical devices',
    description: 'ISO 13485:2016 specifies requirements for a quality management system where an organization needs to demonstrate its ability to provide medical devices and related services that consistently meet customer and applicable regulatory requirements.',
    detailedDescription: 'ISO 13485:2016 is designed to be used by organizations involved in the design, production, installation and servicing of medical devices and the design, development and provision of related services. It represents a comprehensive quality management system for the medical device industry that emphasizes regulatory compliance and risk management.',

    color: {
      text: 'text-pink-600',
      bg: 'bg-pink-50',
      border: 'border-pink-200',
      gradient: 'from-pink-600 via-pink-700 to-rose-800',
      hover: 'hover:bg-pink-100'
    },

    icon: Shield,

    trainingDuration: {
      awareness: '1 day',
      internalAuditor: '2 days',
      leadAuditor: '5 days'
    },

    benefits: [
      'Compliance with medical device regulations',
      'Enhanced product safety and effectiveness',
      'Improved risk management for medical devices',
      'Access to global medical device markets',
      'Increased stakeholder confidence',
      'Better regulatory submission success',
      'Reduced product recalls and failures',
      'Improved customer satisfaction in healthcare'
    ],

    whoShouldAttend: [
      {
        title: 'Quality Managers',
        description: 'Medical device quality assurance managers',
        icon: 'Activity'
      },
      {
        title: 'Regulatory Affairs',
        description: 'Regulatory affairs and compliance professionals',
        icon: 'FileText'
      },
      {
        title: 'R&D Professionals',
        description: 'Design and development engineers',
        icon: 'Microscope'
      },
      {
        title: 'Manufacturing Staff',
        description: 'Production and process managers',
        icon: 'Package'
      }
    ],

    keyTopics: {
      awareness: [
        'Introduction to ISO 13485:2016',
        'Medical device regulations overview',
        'Risk management in medical devices (ISO 14971)',
        'Design controls fundamentals',
        'Post-market surveillance basics',
        'Regulatory requirements overview',
        'Medical device documentation',
        'Quality culture in medical devices'
      ],
      internalAuditor: [
        'ISO 13485:2016 detailed requirements',
        'Risk management verification',
        'Design and development controls audit',
        'Purchasing and supplier controls',
        'Production and process controls',
        'Post-market surveillance review',
        'Medical device specific audit techniques',
        'Regulatory compliance verification'
      ],
      leadAuditor: [
        'Advanced medical device auditing',
        'Leading medical device QMS audits',
        'Complex regulatory compliance assessment',
        'Design control evaluation',
        'Supplier audit management',
        'Post-market surveillance audit',
        'Medical device auditor certification',
        'Certification examination'
      ]
    },

    seo: {
      metaTitle: 'ISO 13485:2016 Training - Medical Devices Quality Management Certification | ISDE Academy',
      metaDescription: 'Professional ISO 13485:2016 Medical Devices Quality Management System training. Regulatory compliance and quality certification for medical device industry.',
      keywords: 'ISO 13485 training, medical device quality management, medical device certification, ISO 13485 auditor, medical device regulations, quality system medical devices India',
      ogImage: '/images/standards/iso-13485-og.jpg'
    }
  },

  'iso-22000': {
    slug: 'iso-22000',
    code: 'ISO 22000:2018',
    name: 'Food Safety Management System',
    shortDescription: 'Ensure food safety throughout the food chain',
    description: 'ISO 22000:2018 specifies requirements for a food safety management system that combines HACCP principles with prerequisite programs to ensure food safety throughout the food chain.',
    detailedDescription: 'ISO 22000:2018 maps out what an organization needs to do to demonstrate its ability to control food safety hazards in order to ensure that food is safe. It can be used by any organization regardless of its size or position in the food chain, from feed producers, primary producers, food manufacturers, transport and storage operators, subcontractors to retail and food service outlets.',

    color: {
      text: 'text-orange-600',
      bg: 'bg-orange-50',
      border: 'border-orange-200',
      gradient: 'from-orange-600 via-orange-700 to-amber-800',
      hover: 'hover:bg-orange-100'
    },

    icon: Shield,

    trainingDuration: {
      awareness: '1 day',
      internalAuditor: '2 days',
      leadAuditor: '5 days'
    },

    benefits: [
      'Enhanced food safety and reduced contamination risks',
      'Compliance with food safety regulations',
      'Improved supply chain management',
      'Increased consumer confidence',
      'Access to international food markets',
      'Better hazard control and HACCP implementation',
      'Reduced food safety incidents and recalls',
      'Improved traceability in food chain'
    ],

    whoShouldAttend: [
      {
        title: 'Food Safety Managers',
        description: 'Food safety and quality managers',
        icon: 'Utensils'
      },
      {
        title: 'HACCP Team Members',
        description: 'HACCP coordinators and team members',
        icon: 'ClipboardCheck'
      },
      {
        title: 'Production Managers',
        description: 'Food production and processing staff',
        icon: 'Factory'
      },
      {
        title: 'Quality Assurance',
        description: 'QA and QC personnel in food industry',
        icon: 'CheckCircle'
      }
    ],

    keyTopics: {
      awareness: [
        'Introduction to ISO 22000:2018',
        'Food safety fundamentals and hazards',
        'HACCP principles overview',
        'Prerequisite programs (PRPs)',
        'Food safety policy and culture',
        'Traceability and recall procedures',
        'Food safety legislation basics',
        'Benefits of food safety management'
      ],
      internalAuditor: [
        'ISO 22000:2018 requirements in detail',
        'HACCP plan verification',
        'Prerequisite programs audit',
        'Operational prerequisite programs (OPRPs)',
        'Hazard analysis and risk assessment',
        'Traceability system verification',
        'Food safety monitoring and measurement',
        'Food safety audit techniques'
      ],
      leadAuditor: [
        'Advanced food safety auditing',
        'Leading food safety management system audits',
        'Complex HACCP system assessment',
        'Supply chain food safety audit',
        'Critical control point verification',
        'Food defense and food fraud assessment',
        'Professional food safety auditor certification',
        'Certification examination'
      ]
    },

    seo: {
      metaTitle: 'ISO 22000:2018 Training - Food Safety Management Certification | ISDE Academy',
      metaDescription: 'Professional ISO 22000:2018 Food Safety Management System training. HACCP certification and food safety programs for the food industry.',
      keywords: 'ISO 22000 training, food safety management, HACCP training, food safety certification, ISO 22000 auditor, food safety India, FSMS certification',
      ogImage: '/images/standards/iso-22000-og.jpg'
    }
  },

  'iso-50001': {
    slug: 'iso-50001',
    code: 'ISO 50001:2018',
    name: 'Energy Management System',
    shortDescription: 'Improve energy performance and reduce costs',
    description: 'ISO 50001:2018 provides a framework for organizations to develop an energy management system to improve energy performance, efficiency, and reduce greenhouse gas emissions.',
    detailedDescription: 'ISO 50001:2018 supports organizations in all sectors to use energy more efficiently through the development of an energy management system. The standard specifies requirements for establishing, implementing, maintaining and improving an energy management system, enabling an organization to achieve continual improvement of energy performance including energy efficiency, energy use and consumption.',

    color: {
      text: 'text-emerald-600',
      bg: 'bg-emerald-50',
      border: 'border-emerald-200',
      gradient: 'from-emerald-600 via-emerald-700 to-green-800',
      hover: 'hover:bg-emerald-100'
    },

    icon: Shield,

    trainingDuration: {
      awareness: '1 day',
      internalAuditor: '2 days',
      leadAuditor: '5 days'
    },

    benefits: [
      'Reduced energy costs and consumption',
      'Lower carbon footprint and emissions',
      'Improved energy efficiency and performance',
      'Compliance with energy regulations',
      'Enhanced corporate sustainability reputation',
      'Better energy data management',
      'Systematic approach to energy improvement',
      'Integration with other management systems'
    ],

    whoShouldAttend: [
      {
        title: 'Energy Managers',
        description: 'Energy managers and coordinators',
        icon: 'Zap'
      },
      {
        title: 'Facility Managers',
        description: 'Facility and building management staff',
        icon: 'Building'
      },
      {
        title: 'Sustainability Officers',
        description: 'Sustainability and environmental professionals',
        icon: 'Leaf'
      },
      {
        title: 'Operations Managers',
        description: 'Plant and operations management',
        icon: 'Settings'
      }
    ],

    keyTopics: {
      awareness: [
        'Introduction to ISO 50001:2018',
        'Energy management fundamentals',
        'Energy review and baseline',
        'Energy performance indicators (EnPIs)',
        'Energy policy and objectives',
        'Significant energy uses (SEUs)',
        'Energy action plans',
        'Benefits of energy management'
      ],
      internalAuditor: [
        'ISO 50001:2018 detailed requirements',
        'Energy review audit techniques',
        'Energy baseline and EnPI verification',
        'Operational controls for energy',
        'Energy monitoring and measurement',
        'Energy performance evaluation',
        'Energy management audit exercises',
        'Energy audit reporting'
      ],
      leadAuditor: [
        'Advanced energy management auditing',
        'Leading energy management system audits',
        'Complex energy performance assessment',
        'Energy data analysis and verification',
        'Integration with environmental management',
        'Energy management best practices',
        'Professional energy auditor certification',
        'Certification examination'
      ]
    },

    seo: {
      metaTitle: 'ISO 50001:2018 Training - Energy Management System Certification | ISDE Academy',
      metaDescription: 'Professional ISO 50001:2018 Energy Management System training. Energy efficiency certification for sustainable business and cost reduction.',
      keywords: 'ISO 50001 training, energy management system, EnMS certification, energy efficiency training, ISO 50001 auditor, energy management India, sustainability training',
      ogImage: '/images/standards/iso-50001-og.jpg'
    }
  },

  'iso-21001': {
    slug: 'iso-21001',
    code: 'ISO 21001:2018',
    name: 'Educational Organizations Management',
    shortDescription: 'Enhance learning outcomes and educational quality',
    description: 'ISO 21001:2018 specifies requirements for a management system for educational organizations to enhance learner satisfaction and meet educational objectives more effectively.',
    detailedDescription: 'ISO 21001:2018 provides a common management tool for educational organizations seeking to provide educational products and services that meet learner and other beneficiary requirements. It can be applied to any organization that uses a curriculum to support the development of competence through teaching, learning or research, regardless of the type, size or method of delivery.',

    color: {
      text: 'text-indigo-600',
      bg: 'bg-indigo-50',
      border: 'border-indigo-200',
      gradient: 'from-indigo-600 via-indigo-700 to-purple-800',
      hover: 'hover:bg-indigo-100'
    },

    icon: Shield,

    trainingDuration: {
      awareness: '1 day',
      internalAuditor: '1 day',
      leadAuditor: '5 days'
    },

    benefits: [
      'Improved educational quality and outcomes',
      'Enhanced learner satisfaction and engagement',
      'Better alignment with educational objectives',
      'Increased stakeholder confidence',
      'Systematic approach to educational excellence',
      'Improved resource utilization',
      'Better curriculum development and delivery',
      'Enhanced institutional reputation'
    ],

    whoShouldAttend: [
      {
        title: 'Educational Leaders',
        description: 'Principals, deans, and academic directors',
        icon: 'GraduationCap'
      },
      {
        title: 'Quality Assurance',
        description: 'Academic quality assurance personnel',
        icon: 'Award'
      },
      {
        title: 'Faculty Members',
        description: 'Teachers, professors, and instructors',
        icon: 'BookOpen'
      },
      {
        title: 'Administrative Staff',
        description: 'Educational administrators and coordinators',
        icon: 'Users'
      }
    ],

    keyTopics: {
      awareness: [
        'Introduction to ISO 21001:2018',
        'Educational organization context',
        'Learner-centered approach',
        'Additional beneficiaries in education',
        'Educational policy and objectives',
        'Competence and learning outcomes',
        'Educational resource management',
        'Benefits for educational institutions'
      ],
      internalAuditor: [
        'ISO 21001:2018 requirements for educational organizations',
        'Educational service planning and delivery audit',
        'Learner satisfaction assessment',
        'Curriculum design and development review',
        'Educational resource adequacy',
        'Learning outcomes evaluation',
        'Educational quality audit techniques',
        'Education-specific audit reporting'
      ],
      leadAuditor: [
        'Advanced educational organization auditing',
        'Leading educational management system audits',
        'Complex learning outcomes assessment',
        'Stakeholder engagement in education',
        'Educational service excellence evaluation',
        'Academic quality assurance audit',
        'Professional educational auditor certification',
        'Certification examination'
      ]
    },

    seo: {
      metaTitle: 'ISO 21001:2018 Training - Educational Organizations Management Certification | ISDE Academy',
      metaDescription: 'Professional ISO 21001:2018 Educational Organizations Management System training. Quality management for schools, colleges, and training institutions.',
      keywords: 'ISO 21001 training, educational organization management, EOMS certification, education quality management, ISO 21001 auditor, educational institutions India',
      ogImage: '/images/standards/iso-21001-og.jpg'
    }
  },

  'iatf-16949': {
    slug: 'iatf-16949',
    code: 'IATF 16949:2016',
    name: 'Automotive Quality Management',
    shortDescription: 'Meet automotive industry quality requirements',
    description: 'IATF 16949:2016 defines the quality management system requirements for the design, development, production, and installation of automotive-related products, emphasizing defect prevention and variation reduction.',
    detailedDescription: 'IATF 16949:2016 was jointly developed by the International Automotive Task Force (IATF) members and submitted to the International Organization for Standardization (ISO). It aligns existing American, German, French, and Italian automotive quality standards within the global automotive industry. The standard emphasizes the development of a process-oriented quality management system that provides for continual improvement, defect prevention, and reduction of variation and waste in the supply chain.',

    color: {
      text: 'text-gray-700',
      bg: 'bg-gray-50',
      border: 'border-gray-300',
      gradient: 'from-gray-700 via-gray-800 to-slate-900',
      hover: 'hover:bg-gray-100'
    },

    icon: Shield,

    trainingDuration: {
      awareness: '1 day',
      internalAuditor: '2 days',
      leadAuditor: '5 days'
    },

    benefits: [
      'Compliance with automotive industry requirements',
      'Reduced waste and improved efficiency',
      'Enhanced supplier quality and performance',
      'Access to global automotive supply chains',
      'Systematic approach to continuous improvement',
      'Better process capability and performance',
      'Reduced customer complaints and returns',
      'Improved product development process'
    ],

    whoShouldAttend: [
      {
        title: 'Quality Managers',
        description: 'Automotive quality managers and coordinators',
        icon: 'Car'
      },
      {
        title: 'Supplier Development',
        description: 'Supplier quality and development engineers',
        icon: 'Package'
      },
      {
        title: 'Manufacturing Engineers',
        description: 'Production and process engineers',
        icon: 'Cog'
      },
      {
        title: 'Product Development',
        description: 'Design and development professionals',
        icon: 'Wrench'
      }
    ],

    keyTopics: {
      awareness: [
        'Introduction to IATF 16949:2016',
        'Automotive industry requirements overview',
        'Core tools introduction (APQP, FMEA, MSA, SPC, PPAP)',
        'Customer-specific requirements',
        'Product safety and recall procedures',
        'Automotive process approach',
        'Supplier development fundamentals',
        'Benefits in automotive industry'
      ],
      internalAuditor: [
        'IATF 16949:2016 detailed requirements',
        'Automotive core tools verification',
        'Product realization process audit',
        'Manufacturing process assessment',
        'Control plan and FMEA review',
        'Customer-specific requirements compliance',
        'Automotive-specific audit techniques',
        'Layered process audits (LPA)'
      ],
      leadAuditor: [
        'Advanced automotive quality system auditing',
        'Leading IATF 16949 audit teams',
        'Complex automotive process assessment',
        'Supplier audit and development',
        'Customer-specific requirements mastery',
        'Automotive industry best practices',
        'Professional automotive auditor certification',
        'Certification examination'
      ]
    },

    seo: {
      metaTitle: 'IATF 16949:2016 Training - Automotive Quality Management Certification | ISDE Academy',
      metaDescription: 'Professional IATF 16949:2016 Automotive Quality Management System training. Core tools and automotive quality certification for supply chain excellence.',
      keywords: 'IATF 16949 training, automotive quality management, automotive certification, IATF 16949 auditor, automotive core tools, automotive quality India, supply chain quality',
      ogImage: '/images/standards/iatf-16949-og.jpg'
    }
  }
};

/**
 * Get all standards as an array
 */
export const getAllStandards = () => Object.values(standards);

/**
 * Get a specific standard by slug
 */
export const getStandardBySlug = (slug) => standards[slug];

/**
 * Get standards by category/type (future enhancement)
 */
export const getStandardsByType = (type) => {
  const typeMap = {
    quality: ['iso-9001', 'iso-13485', 'iatf-16949'],
    environmental: ['iso-14001', 'iso-50001'],
    safety: ['iso-45001', 'iso-22000'],
    security: ['iso-27001'],
    other: ['iso-21001']
  };

  return (typeMap[type] || []).map(slug => standards[slug]);
};

/**
 * Search standards by keyword
 */
export const searchStandards = (keyword) => {
  const lowerKeyword = keyword.toLowerCase();
  return getAllStandards().filter(standard =>
    standard.name.toLowerCase().includes(lowerKeyword) ||
    standard.code.toLowerCase().includes(lowerKeyword) ||
    standard.description.toLowerCase().includes(lowerKeyword)
  );
};
